import { useState, useMemo, Fragment, useCallback } from 'react'

import { numberFormat, dateTimeStampFormat } from 'shared/util'
import { TextMatch } from 'shared/text'
import { useUserSession } from 'features/session/context'

import {
  Box,
  Stack,
  Button,
  Typography,
  Card,
  CardHeader,
  Avatar,
  CardActionArea,
  CardActions,
  Grid,
  Divider,
  BoxProps
} from '@mui/material'

import { QueryStats as TargetListIcon } from '@mui/icons-material'
import { blue, green } from '@mui/material/colors'
import { EditIcon, TrushIcon, PlusIcon } from 'shared/icons'

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { SearchWordField } from 'components/controls/searchWordField'
import { ValueSpan } from 'components/controls/valueSpan'
import { ConfirmDialog } from 'components/dialog/confirm'

import { SearchResult, SearchStatus } from 'interface/search'
import { TargetListResult, useDeleteTargetList, useListTargetList } from 'interface/search/targetList'

import { EntryTargetListDialog } from 'features/search/targetList/dialog/entryTargetListDialog'
import { EditTargetListDialog } from 'features/search/targetList/dialog/editTargetListDialog'
import { AddTargetDialog } from 'features/search/targetList/dialog/addTargetDialog'
import { SearchResultBlocks } from 'features/search/components/resultBlocks'

type TargetListCardProps = {
  record: TargetListResult
  onAddTargetList: (record: TargetListResult) => void
  onEditTargetList: (record: TargetListResult) => void
  onDeleteTargetList: (record: TargetListResult) => void
  openTargetList: (listId: number, isOpenGrid: boolean) => void
}
const TargetListCard = ({
  record,
  onAddTargetList,
  onEditTargetList,
  onDeleteTargetList,
  openTargetList
}: TargetListCardProps) => {
  const { isGeneral } = useUserSession()
  return (
    <Card>
      <CardActionArea
        onClick={() => openTargetList(record.listId, false)}
      >
        <CardHeader
          avatar={
            <Avatar
              sx={{
                bgcolor: record.extendCount > 0 ? green[500] : blue[500],
              }}
            >
              <TargetListIcon />
            </Avatar>
          }
          title={
            <Typography
              variant="body1"
              color="text.primary"
            >
              {record.listName}
            </Typography>
          }
          subheader={
            <Fragment>
              <Stack direction="column">
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {dateTimeStampFormat(record.updateTime)}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {record.sourceCount > 0 && (
                    <ValueSpan
                      text={numberFormat(record.sourceCount)}
                      suffix="人"
                    />
                  )}
                  {record.extendCount > 0 && (
                    <ValueSpan
                      prefix="拡張"
                      text={numberFormat(record.extendCount)}
                      suffix="人"
                    />
                  )}
                </Stack>

                {record.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    whiteSpace="pre-wrap"
                  >
                    {record.description}
                  </Typography>
                )}
              </Stack>
            </Fragment>
          }
        />
      </CardActionArea>

      <CardActions style={{ justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => {
            openTargetList(record.listId, true)
          }}
        >
          シート分析
        </Button>

        <Box flexGrow={1} />

        <Button
          startIcon={<PlusIcon />}
          disabled={isGeneral === false}
          variant="text"
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => onAddTargetList(record)}
        >
          追加
        </Button>
        <Button
          startIcon={<EditIcon />}
          disabled={isGeneral === false}
          variant="text"
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => onEditTargetList(record)}
        >
          編集
        </Button>
        <Button
          startIcon={<TrushIcon />}
          disabled={isGeneral === false}
          variant="text"
          size="small"
          color="error"
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => onDeleteTargetList(record)}
        >
          削除
        </Button>
      </CardActions>
    </Card>

  )
}

type SearchTargetPageProps = {
  search: SearchResult
  targetListState: ReturnType<typeof useListTargetList>
  onChangeSearch: () => void
  openSisetu: () => void
  openKojin: () => void
  openGrid: () => void
  openTargetList: (listId: number, isOpenGrid: boolean) => void
} & BoxProps
export const SearchTargetPage = ({
  search,
  targetListState,
  onChangeSearch,
  openSisetu,
  openKojin,
  openGrid,
  openTargetList,
  ...others
}: SearchTargetPageProps) => {
  const { isGeneral } = useUserSession()

  const [modal, setModal] = useState<{
    command: 'newTargetList' | 'addTarget' | 'editTargetList' | 'deleteTargetList'
    target?: TargetListResult
  } | null>(null)
  const [searchWord, setSearchWord] = useState<string>('')

  const filteredList = useMemo<TargetListResult[] | null>(() => {
    if (!targetListState.data) return null
    if (searchWord !== '') {
      const match = new TextMatch(searchWord)
      return targetListState.data.filter((r) => match.match(r.listName))
    } else {
      return targetListState.data
    }
  }, [targetListState, searchWord])

  const onDeleted = useCallback(
    () => {
      setModal(null)
      onChangeSearch()
    },
    [setModal, onChangeSearch]
  )
  const [deleteState, deleteTargetList] = useDeleteTargetList(search.searchId, { onDeleted })

  return (
    <Box {...others} position="relative" overflow="hidden">
      {targetListState.loading && <Loading />}

      <Stack direction="column" paddingTop={1} spacing={1} height="100%" boxSizing="border-box" overflow="auto">
        {targetListState.error && <ErrorInfo error={targetListState.error} />}

        {search.status === SearchStatus.Finished ? (
          <>
            <SearchResultBlocks
              paddingX={1}
              search={search}
              openSisetu={openSisetu}
              openKojin={openKojin}
              openGrid={openGrid}
            />
            <Divider/>
          </>
        ) : <></>}

        <Stack
          direction="row"
          spacing={1}
          paddingX={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            disabled={!isGeneral}
            variant="contained"
            size="medium"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => setModal({ command: 'newTargetList' })}
            startIcon={<PlusIcon/>}
          >
            ターゲットリスト登録
          </Button>
          <Box flexGrow={1}/>
          
          <SearchWordField
            id="search-word-search"
            searchWord={searchWord}
            onChangeSearchWord={setSearchWord}
          />
        </Stack>

        <Divider/>

        <Box flexGrow={1} paddingX={1} paddingBottom={1} overflow="auto">
          <Grid container spacing={1}>
            {filteredList?.map((record, i) => (
              <Grid
                key={`targetList-${record.listId}-${i}`}
                size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}
              >
                <TargetListCard
                  record={record}
                  onAddTargetList={record => setModal({ command: 'addTarget', target: record })}
                  onEditTargetList={record => setModal({ command: 'editTargetList', target: record })}
                  onDeleteTargetList={record => setModal({ command: 'deleteTargetList', target: record })}
                  openTargetList={openTargetList}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {modal?.command === 'newTargetList' ? (
          <EntryTargetListDialog
            searchId={search.searchId}
            onSubmit={() => {
              setModal(null)
              onChangeSearch()
            }}
            onClose={() => setModal(null)}
          />
        ) : modal?.command === 'addTarget' && modal?.target ? (
          <AddTargetDialog
            searchId={search.searchId}
            targetList={modal.target}
            onSubmit={() => {
              setModal(null)
              onChangeSearch()
            }}
            onClose={() => setModal(null)}
          />
        ) : modal?.command === 'editTargetList' && modal?.target ? (
          <EditTargetListDialog
            searchId={search.searchId}
            targetList={modal.target}
            onSubmit={() => {
              setModal(null)
              onChangeSearch()
            }}
            onClose={() => setModal(null)}
          />
        ) : modal?.command === 'deleteTargetList' && modal?.target ? (
          <ConfirmDialog
            isLoading={deleteState.posting}
            message={`${modal.target.listName}を削除します。よろしいですか？`}
            ok={{
              label: 'はい',
              onClick: () => {
                const listId = modal?.target?.listId
                if (!listId) return
                deleteTargetList(listId)
              }
            }}
            cancel={{
              label: 'いいえ',
              onClick: () => setModal(null)
            }}
          />
        ) : (
          <></>
        )}
      </Stack>
    </Box>
  )
}
